import { memo, React, useCallback, useEffect, useMemo, useState } from 'react'
import {
  CheckOutlined,
  CloseOutlined,
  CopyTwoTone,
  DeleteOutlined,
  DragOutlined,
  EditOutlined,
  ExportOutlined,
  TranslationOutlined,
} from '@ant-design/icons'
import {
  Button,
  Form,
  Input,
  Popconfirm,
  Space,
  Tooltip,
  Typography,
} from 'antd'
import { isEmpty, isEqual, isNil, map, values } from 'lodash'
import PropTypes from 'prop-types'

import { areEqualByProps } from '../../../core/utils'

import { SearchHighlightedSpan } from '../../../common/components/sections'
import { StyledFullWidthSelect } from '../../../common/components/styled'
import { DROPDOWN_MIN_WIDTH, PERMISSIONS } from '../../../common/constants'
import { useUserHasPermissions } from '../../../common/hooks'
import { filterSelectOptions } from '../../../common/utils'

import { DICTIONARY_WORD_TYPES } from '../../constants'
import { dictionaryWordPropType } from '../../prop-types'

import DictionaryWordTransferToIngredientKeywords from './dictionary-word-transfer-to-ingredient-keywords'

const DictionaryWordNodeTitle = ({
  node,
  searchValue,
  editable,
  onRename,
  onDelete,
  onChangeLanguage,
  onChangeType,
  language,
  languageOptions,
  refetchLastEvent,
}) => {
  const [nodeTerm, setNodeTerm] = useState(node.term)
  const [isCopied, setIsCopied] = useState(false)
  const [isRenameInputShown, setIsRenameInputShown] = useState(false)
  const [isLanguageSelectShown, setIsLanguageSelectShown] = useState(false)
  const [isTypeSelectShown, setIsTypeSelectShown] = useState(false)
  const [isTransferModalShown, setIsTransferModalShown] = useState(false)

  const hasEditPermission = useUserHasPermissions(
    PERMISSIONS.DICTIONARY_WORD.UPDATE,
  )

  const hasDeletePermission = useUserHasPermissions(
    PERMISSIONS.DICTIONARY_WORD.DELETE,
  )

  const dictionaryTypeOptions = useMemo(
    () =>
      map(values(DICTIONARY_WORD_TYPES), ({ value, label }) => ({
        value,
        label,
      })),
    [],
  )

  const isRenameButtonShown = useMemo(
    () => editable && hasEditPermission && !isNil(onRename),
    [editable, hasEditPermission, onRename],
  )

  const isDeleteButtonShown = useMemo(
    () => !isNil(onDelete) && hasDeletePermission && editable,
    [editable, hasDeletePermission, onDelete],
  )

  const isChangeLanguageButtonShown = useMemo(
    () => editable && hasEditPermission && !isNil(onChangeLanguage),
    [editable, hasEditPermission, onChangeLanguage],
  )

  const isChangeTypeButtonShown = useMemo(
    () => !isNil(onChangeType) && hasEditPermission && editable,
    [editable, hasEditPermission, onChangeType],
  )

  const isTransferToIngredientKeywordsButtonShown = useMemo(
    () => editable && hasEditPermission,
    [editable, hasEditPermission],
  )

  const handleOnDelete = useCallback(
    () => onDelete(node.id),
    [node.id, onDelete],
  )

  const handleConfirmRename = useCallback(() => {
    onRename(node.id, nodeTerm)
    setIsRenameInputShown(false)
  }, [node, nodeTerm, onRename])

  const handleShowRenameInput = useCallback(() => {
    setIsRenameInputShown(true)
  }, [])

  const handleShowLanguageSelect = useCallback(() => {
    setIsLanguageSelectShown(true)
  }, [])

  const handleShowTypeSelect = useCallback(() => {
    setIsTypeSelectShown(true)
  }, [])

  const handleShowTransferModal = useCallback(() => {
    setIsTransferModalShown(true)
  }, [])

  const handleHideTransferModal = useCallback(() => {
    setIsTransferModalShown(false)
  }, [])

  const handleCancel = useCallback(() => {
    setNodeTerm(node.term)
    setIsRenameInputShown(false)
    setIsLanguageSelectShown(false)
    setIsTypeSelectShown(false)
  }, [node.term])

  const handleOnRename = useCallback(event => {
    setNodeTerm(event.target.value)
  }, [])

  const handleOnChangeLanguage = useCallback(
    value => {
      onChangeLanguage(node.id, value ?? null)
    },
    [node.id, onChangeLanguage],
  )

  const handleOnChangeType = useCallback(
    value => {
      onChangeType(node.id, value, node.type)
    },
    [node.id, node.type, onChangeType],
  )

  const copyScraperQualityTrackerData = useCallback(() => {
    navigator.clipboard.writeText(nodeTerm)
    setIsCopied(true)
  }, [nodeTerm])

  const nodeText = (
    <SearchHighlightedSpan searchValue={searchValue}>
      {node.term}
    </SearchHighlightedSpan>
  )

  useEffect(() => {
    let copiedTimeout

    if (isCopied) {
      copiedTimeout = setTimeout(() => setIsCopied(false), 2000)
    }

    return () => clearTimeout(copiedTimeout)
  }, [isCopied])

  if (!isRenameInputShown && !isLanguageSelectShown && !isTypeSelectShown) {
    return (
      <Space>
        {nodeText}
        {!isEmpty(language) && (
          <Typography.Text>({language.label})</Typography.Text>
        )}

        {isCopied ? (
          <Tooltip title="Copied">
            <CheckOutlined style={{ color: '#52c41a' }} />
          </Tooltip>
        ) : (
          isRenameButtonShown && (
            <Tooltip title="Copy keyword">
              <CopyTwoTone onClick={copyScraperQualityTrackerData} />
            </Tooltip>
          )
        )}
        {isRenameButtonShown && (
          <Tooltip title="Edit name">
            <Button
              onClick={handleShowRenameInput}
              size="small"
              shape="circle"
              icon={<EditOutlined />}
            />
          </Tooltip>
        )}

        {isChangeLanguageButtonShown && (
          <Tooltip title="Edit language">
            <Button
              onClick={handleShowLanguageSelect}
              size="small"
              shape="circle"
              icon={<TranslationOutlined />}
            />
          </Tooltip>
        )}

        {isChangeTypeButtonShown && (
          <Tooltip title="Move to another type">
            <Button
              onClick={handleShowTypeSelect}
              size="small"
              shape="circle"
              icon={<DragOutlined />}
            />
          </Tooltip>
        )}

        {isTransferToIngredientKeywordsButtonShown && (
          <>
            <Tooltip title="Transfer to ingredient">
              <Button
                onClick={handleShowTransferModal}
                size="small"
                shape="circle"
                icon={<ExportOutlined />}
              />
            </Tooltip>
            {isTransferModalShown && (
              <DictionaryWordTransferToIngredientKeywords
                node={node}
                languageOptions={languageOptions}
                onClose={handleHideTransferModal}
                refetchLastEvent={refetchLastEvent}
              />
            )}
          </>
        )}

        {isDeleteButtonShown && (
          <Popconfirm
            title="Are you sure you want to delete?"
            onConfirm={handleOnDelete}
          >
            <Button
              size="small"
              danger
              shape="circle"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        )}
      </Space>
    )
  }

  return (
    <Space>
      {isRenameInputShown ? (
        <>
          <Form.Item
            validateStatus={isEmpty(nodeTerm) ? 'error' : ''}
            help={isEmpty(nodeTerm) ? 'Please input Term!' : ''}
          >
            <Input
              value={nodeTerm}
              placeholder="Input term..."
              onChange={handleOnRename}
            />
          </Form.Item>

          <Button
            size="small"
            shape="circle"
            icon={<CheckOutlined />}
            onClick={handleConfirmRename}
          />
        </>
      ) : (
        <>
          {nodeText}
          {isLanguageSelectShown && (
            <StyledFullWidthSelect
              showSearch
              allowClear
              style={{ minWidth: 50 }}
              defaultValue={language?.value}
              filterOption={filterSelectOptions}
              options={languageOptions}
              onChange={handleOnChangeLanguage}
              placeholder="Select a language..."
              dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
            />
          )}
          {isTypeSelectShown && (
            <StyledFullWidthSelect
              defaultValue={node.type}
              showSearch
              filterOption={filterSelectOptions}
              options={dictionaryTypeOptions}
              onChange={handleOnChangeType}
              placeholder="Select a type..."
              dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
            />
          )}
        </>
      )}
      <Button
        size="small"
        shape="circle"
        icon={<CloseOutlined />}
        onClick={handleCancel}
      />
    </Space>
  )
}

DictionaryWordNodeTitle.propTypes = {
  node: dictionaryWordPropType.isRequired,
  searchValue: PropTypes.string,
  editable: PropTypes.bool,
  onRename: PropTypes.func,
  onDelete: PropTypes.func,
  onChangeLanguage: PropTypes.func,
  onChangeType: PropTypes.func,
  language: PropTypes.object,
  languageOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  refetchLastEvent: PropTypes.func.isRequired,
}

DictionaryWordNodeTitle.defaultProps = {
  searchValue: '',
  editable: false,
  onRename: undefined,
  onDelete: undefined,
  onChangeLanguage: undefined,
  onChangeType: undefined,
  language: undefined,
}

export default memo(
  DictionaryWordNodeTitle,
  ({ node: prevNode, ...restPrevious }, { node: nextNode, ...restNext }) =>
    isEqual(prevNode, nextNode) &&
    areEqualByProps(restPrevious, restNext, [
      'searchValue',
      'editable',
      'languageOptions',
      'language',
    ]),
)
